/**
 * Auth State Store
 * Zustand store for managing authentication state.
 *
 * Security: JWT tokens are stored in HttpOnly cookies (set by backend).
 * This store only tracks user info and authentication state, NOT tokens.
 * Tokens cannot be accessed via JavaScript due to HttpOnly flag (XSS protection).
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import axios from 'axios';
import i18n from '../i18n';

interface User {
  id: string;
  email: string;
  name: string;
  avatarUrl: string | null;
}

interface AuthState {
  // State
  user: User | null;
  isAuthenticated: boolean;

  // Actions
  login: (user: User) => void;
  logout: (showToast?: boolean) => void;
  setUser: (user: User) => void;
  clearAuth: () => void;
}

// Session expiration message - uses i18n for translation
const getSessionExpiredMessage = () => i18n.t('auth.sessionExpiredMessage');

// Keys to clear on logout (non-auth related app state)
const STORAGE_KEYS_TO_CLEAR = [
  'auth_token', // Legacy key - keep for cleanup
  'ff_authenticated', // Legacy key - keep for cleanup
  'financeflow-ui',
  'financeflow-budget',
  'financeflow-auth',
];

/**
 * Clears all auth-related data from browser storage
 */
const clearAllStorage = () => {
  // Clear specific localStorage keys
  STORAGE_KEYS_TO_CLEAR.forEach((key) => {
    localStorage.removeItem(key);
  });

  // Clear sessionStorage entirely
  sessionStorage.clear();
};

/**
 * Redirect to login page using replace to prevent back navigation
 */
const redirectToLogin = () => {
  // Use replace to prevent going back to protected page
  window.location.replace('/');
};

/**
 * Call backend to clear the HttpOnly auth cookie
 */
const clearAuthCookie = async (): Promise<void> => {
  try {
    await axios.post('/api/auth/logout', {}, { withCredentials: true });
  } catch {
    // Ignore errors - cookie might already be cleared
    console.warn('Logout API call failed, cookie may already be cleared');
  }
};

// Event for notifying components about session expiration
export const AUTH_EVENTS = {
  SESSION_EXPIRED: 'auth:session_expired',
  LOGOUT: 'auth:logout',
} as const;

export const dispatchAuthEvent = (event: keyof typeof AUTH_EVENTS, detail?: unknown) => {
  window.dispatchEvent(new CustomEvent(AUTH_EVENTS[event], { detail }));
};

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      isAuthenticated: false,

      // Login action - only stores user info (token is in HttpOnly cookie)
      login: (user: User) => {
        set({
          user,
          isAuthenticated: true,
        });
      },

      // Logout action - complete cleanup including backend cookie clear
      logout: async (showToast = true) => {
        const wasAuthenticated = get().isAuthenticated;

        // Reset state immediately
        set({
          user: null,
          isAuthenticated: false,
        });

        // Clear all local storage
        clearAllStorage();

        // Clear the HttpOnly cookie via backend API
        await clearAuthCookie();

        // Dispatch logout event for other components
        if (wasAuthenticated) {
          dispatchAuthEvent('LOGOUT');
        }

        // Show toast notification if requested
        if (showToast) {
          dispatchAuthEvent('SESSION_EXPIRED', { message: getSessionExpiredMessage() });
        }

        // Redirect to login
        redirectToLogin();
      },

      // Update user info
      setUser: (user: User) => {
        set({ user });
      },

      // Clear auth state without redirect (used by response interceptor)
      clearAuth: () => {
        set({
          user: null,
          isAuthenticated: false,
        });
        clearAllStorage();
      },
    }),
    {
      name: 'financeflow-auth',
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);

// Export utility for components
export const isAuthenticated = () => useAuthStore.getState().isAuthenticated;
export const forceLogout = (showToast = true) => useAuthStore.getState().logout(showToast);

/**
 * Verify authentication by calling /api/auth/me
 * Use this on app startup to check if the cookie is still valid
 */
export const verifyAuth = async (): Promise<User | null> => {
  try {
    const response = await axios.get<{ success: boolean; data: User }>('/api/auth/me', {
      withCredentials: true,
    });

    if (response.data.success && response.data.data) {
      const user = response.data.data;
      useAuthStore.getState().login(user);
      return user;
    }
  } catch {
    // Auth failed, clear state
    useAuthStore.getState().clearAuth();
  }
  return null;
};
